import React from 'react';
import { useNotification } from '../../../../hooks/useNotification';
import { useErrorInputs } from '../../../../hooks/useErrorInputs';
import { useForm } from '../../../../hooks/useForm';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Toolbar from '@mui/material/Toolbar';
import AppBar from '@mui/material/AppBar';
import Dialog from '@mui/material/Dialog';
import Slide from '@mui/material/Slide';
import Box from '@mui/material/Box';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';
import { ManageConfiguration } from '../../components/manageconfiguration/ManageConfiguration';
import { ManageFlowProcess } from '../../components/manageFlowProcess/ManageFlowProcess';
import { ActionIconItem } from '../../../../components/others/ActionIconItem';
import { ActionButtons } from '../../../../components/others/ActionButtons';
import { ManageActions } from '../manageconfiguration/ManageActions';
import { normalizeConfig, parseFlowProcess } from '../../helpers/helpers';
import { EMPTY_ACTION, MOCKCONFIG } from '../../constants/interface.constants';
import { PreviewTable } from './PreviewTable';
import { safeParseJSON } from '../../../../utils/jsonValidator';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const PreviewContent = ({ config, json, onItemClick, flowActions = [], handleDirectInputChange, t, openFullPreview }) => {
    const tableColumns = config.table;
    const headerButtons = flowActions.length > 0 ? flowActions : config.button;
    return (
        <Box sx={{ width: '100%', mt: openFullPreview ? 2 : 0, px: 2 }} className='custom-hard-table-zd-nix'>
            <Box sx={{ px: '5px', py: '5px', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                {headerButtons.map((item, i) => <ActionIconItem key={i} item={item} onClick={() => onItemClick(item)} />)}
                {(json.type_action !== 'FLOW_PROCESS' && json.type_action !== 'BUTTON' && json.type_action !== 'API') &&
                    <Box sx={{ width: 'fit-content' }}>
                        <ManageConfiguration
                            data={safeParseJSON(json.configuration)}
                            typeAction={json.type_action}
                            isDisabled={!json?.type_action}
                            isEdit={false}
                            addedConfiguration={handleDirectInputChange}
                            t={t}
                            action={'createButton'}
                        />
                    </Box>
                }
            </Box>
            <PreviewTable columns={tableColumns} fullJson={json} handleToggleFullscreenPreviewEditor={onItemClick} />
        </Box>
    );
};


export const PreviewRenderer = ({ json = {}, onChange, t = (k) => k, isEdit }) => {
    const [values, handleInputChange, handleDirectInputChange, setAllInfo] = useForm(EMPTY_ACTION);
    const [errorInputs, , resetErrors, validForm] = useErrorInputs({});
    const [sendNotification] = useNotification();
    const [openFullPreview, setOpenFullPreview] = React.useState(false);
    const [openActionEditor, setOpenActionEditor] = React.useState(false);
    const [selectedIndex,    setSelectedIndex]    = React.useState(null);

    const config = React.useMemo(() => {
        const parsed = normalizeConfig(json);
        if (!parsed?.table?.length) {
            return MOCKCONFIG;
        }
        return parsed;
    }, [json]);

    const flowActions = React.useMemo(() => {
        if (json.type_action === 'FLOW_PROCESS' || json.type_action === 'BUTTON') {
            const parsed = safeParseJSON(json.configuration);
            return parsed ? [parsed] : MOCKCONFIG.button;
        }
        const parsed = parseFlowProcess(json?.flow_process);
        return parsed?.length ? parsed : MOCKCONFIG.button;
    }, [json?.flow_process, json?.configuration, json?.type_action]);

    const handleItemClick = (action) => {
        const index = flowActions.findIndex(
            (a) => a.accion === action.accion && a.icon === action.icon
        );
        resetErrors();
        setSelectedIndex(index);
        setAllInfo(action);
        setOpenActionEditor(true);
    };

    const handleCloseActionEditor = () => {
        setOpenActionEditor(false);
        resetErrors();
        setAllInfo(EMPTY_ACTION);
        setSelectedIndex(null);
    };

    const handleSaveAction = () => {
        const isValid = validForm(['accion', 'icon', 'color', 'type'], values);
        if (!isValid) {
            sendNotification({ msg: 'Debe completar el formulario.', variant: 'error' });
            return;
        }
        if (typeof onChange === 'function') {
            if (json.type_action === 'FLOW_PROCESS' || json.type_action === 'BUTTON') {
                onChange('configuration', JSON.stringify({ ...flowActions[0], ...values }));
            }else {
                let updatedFlow;
                if (selectedIndex === null) 
                    updatedFlow = [ ...flowActions, values];
                else 
                    updatedFlow = flowActions.map((item, i) => i === selectedIndex ? { ...item, ...values } : item );
                onChange('flow_process', updatedFlow);
            }
        }
        handleCloseActionEditor();
    };

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                {(json.type_action !== 'FLOW_PROCESS' && json.type_action !== 'BUTTON' && json.type_action !== 'API') &&
                    <Box sx={{ width: 'fit-content' }}>
                        <ManageConfiguration
                            data={safeParseJSON(json.configuration)}
                            typeAction={json.type_action}
                            isDisabled={!json?.type_action}
                            isEdit={isEdit}
                            addedConfiguration={onChange}
                            t={t}
                            action={'listColumns'}
                        />
                    </Box>
                }
                {(json.type_action !== 'FLOW_PROCESS' && json.type_action !== 'BUTTON' && json.type_action !== 'API') &&
                    <Box sx={{ width: 'fit-content' }}>
                        <ManageFlowProcess
                            data={safeParseJSON(json.flow_process)}
                            typeAction={json.type_action}
                            isDisabled={!json?.type_action}
                            isEdit={false}
                            addedFlowProcess={onChange}
                            t={t}
                            action={'createAction'}
                        />
                    </Box>
                }
                <Tooltip title={t('interface.fullscreenPreview')}>
                    <IconButton size="small" onClick={() => setOpenFullPreview(true)}>
                        <OpenInFullIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            <PreviewContent config={config} json={json} onItemClick={handleItemClick} flowActions={flowActions} handleDirectInputChange={onChange} t={t} openFullPreview={openFullPreview}/>

            {/* ── Fullscreen del preview completo ────────────────────────── */}
            <Dialog
                fullScreen
                open={openFullPreview}
                onClose={() => setOpenFullPreview(false)}
                TransitionComponent={Transition}
            >
                <AppBar sx={{ position: 'relative' }}>
                    <Toolbar>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                            {json?.name || 'Preview'}
                        </Typography>
                        <Tooltip title={t('closeFullscreenPreview')}>
                            <IconButton edge="end" color="inherit" onClick={() => setOpenFullPreview(false)}>
                                <CloseFullscreenIcon />
                            </IconButton>
                        </Tooltip>
                    </Toolbar>
                </AppBar>
                <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                    <PreviewContent config={config} json={json} onItemClick={handleItemClick} flowActions={flowActions} handleDirectInputChange={handleDirectInputChange} t={t} openFullPreview={openFullPreview} />
                </Box>
            </Dialog>

            {/* ── Editor de acción via formulario ────────────────────────── */}
            <Dialog
                open={openActionEditor}
                onClose={handleCloseActionEditor}
                fullWidth
                maxWidth="md"
                TransitionComponent={Transition}
            >
                <DialogTitle>
                    <Typography variant="h5" component="span">
                        Editar acción
                    </Typography>
                    <IconButton
                        onClick={handleCloseActionEditor}
                        sx={{ position: 'absolute', right: 8, top: 8, color: 'grey.500' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    <ManageActions
                        values={values}
                        errorInputs={errorInputs}
                        listActions={false}
                        handleInputChange={handleInputChange}
                        handleDirectInputChange={handleDirectInputChange}
                        t={t}
                    />
                </DialogContent>

                <DialogActions>
                    <ActionButtons onSubmit={handleSaveAction} onCancel={handleCloseActionEditor} />
                </DialogActions>
            </Dialog>
        </>
    );
};